import api from './client';

export type PerfVerdict = 'crash' | 'delivery' | 'offer' | 'dep' | 'geo_lag' | 'none';

export interface PerfRow {
  appId: string;
  appName: string;
  bundleId: string;
  country: string;
  clicks: number;
  uniqClicks: number;
  regs: number;
  deps: number;
  payoutCents: number;
  fbNewUsers: number | null;
  fbActiveUsers: number | null;
  fbSessions: number | null;
  baseClicks: number;
  baseRegs: number;
  baseDeps: number;
  basePayoutCents: number;
  baseFbNewUsers: number | null;
  crashFreePct: number | null;
  hasFirebase: boolean;
  isBanned: boolean;
  arrival: number | null;
  regRate: number | null;
  depRate: number | null;
  epcCents: number | null;
  baseRegRate: number | null;
  baseDepRate: number | null;
  geoMedianRegRate: number | null;
  benchRegRate: number | null;
  lostRegs: number;
  lostMoneyCents: number;
  verdict: PerfVerdict;
  noisy: boolean;
  fbMissing: boolean;
}

export interface PerfThresholds {
  minClicks: number;
  dropPct: number;
  geoLagPct: number;
  arrivalMinPct: number;
  crashFreePct: number;
}

/** Why an app's arrivals column is empty — see fetchFbHealth on the worker. */
export type FbHealthReason = 'no_link' | 'auth_error' | 'stale';

export interface FbHealthEntry {
  appId: string;
  appName: string;
  reason: FbHealthReason;
  accountName: string | null;
  lastCountrySync: string | null;
  error: string | null;
  clicks: number;
}

export interface FbHealth {
  apps: FbHealthEntry[];
  app_count: number;
  /** Clicks in the period belonging to those apps. */
  clicks: number;
  total_clicks: number;
}

export interface PerfRowsResponse {
  rows: PerfRow[];
  from: string;
  to: string;
  baseline_from: string;
  baseline_to: string;
  thresholds: PerfThresholds;
  fb_health: FbHealth;
}

export interface PerfCampaignRow {
  campaign_id: string;
  campaign_name: string | null;
  label: string | null;
  clicks: number;
  regs: number;
  deps: number;
  payout_cents: number;
}

export interface PerfSeriesPoint {
  date: string;
  clicks: number;
  regs: number;
  deps: number;
  fb_new_users: number | null;
}

/** Tracker traffic that belongs to no app: PWA and landing campaigns. */
export interface NoAppPerfRow {
  campaignId: string;
  campaignName: string | null;
  label: string | null;
  destinationType: string | null;
  country: string;
  clicks: number;
  uniqClicks: number;
  regs: number;
  deps: number;
  payoutCents: number;
}

export interface AppGeoPerfRow {
  appId: string;
  appName: string;
  bundleId: string;
  appCreatedAt: string;
  appReleasedAt: string | null;
  country: string;
  clicks: number;
  uniqClicks: number;
  regs: number;
  deps: number;
  payoutCents: number;
}

export function getPerfRows(q: {
  from: string; to: string; appId?: string; countries?: string[];
}): Promise<PerfRowsResponse> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.appId) params.set('app_id', q.appId);
  if (q.countries?.length) params.set('countries', q.countries.join(','));
  return api.get<PerfRowsResponse>(`/admin/perf/rows?${params.toString()}`);
}

export function getAppGeoPerfRows(q: {
  from: string; to: string; countries?: string[];
}): Promise<AppGeoPerfRow[]> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.countries?.length) params.set('countries', q.countries.join(','));
  return api.get<AppGeoPerfRow[]>(`/admin/perf/app-geo?${params.toString()}`);
}

export function getNoAppPerfRows(q: {
  from: string; to: string; countries?: string[];
}): Promise<NoAppPerfRow[]> {
  const params = new URLSearchParams({ from: q.from, to: q.to });
  if (q.countries?.length) params.set('countries', q.countries.join(','));
  return api.get<NoAppPerfRow[]>(`/admin/perf/no-app?${params.toString()}`);
}

export function getPerfCampaigns(q: {
  from: string; to: string; appId: string; country: string;
}): Promise<PerfCampaignRow[]> {
  const params = new URLSearchParams({ from: q.from, to: q.to, app_id: q.appId, country: q.country });
  return api.get<PerfCampaignRow[]>(`/admin/perf/campaigns?${params.toString()}`);
}

export function getPerfSeries(q: {
  from: string; to: string; appId: string; country: string;
}): Promise<PerfSeriesPoint[]> {
  const params = new URLSearchParams({ from: q.from, to: q.to, app_id: q.appId, country: q.country });
  return api.get<PerfSeriesPoint[]>(`/admin/perf/series?${params.toString()}`);
}
